import { ArdoqId, MetamodelComponentType } from '@ardoq/api-types';
import { IconButton } from '@ardoq/button';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { CellEditor, CellEditorProps } from './types';
import { debounce, uniqBy } from 'lodash';
import { FlexBox } from '@ardoq/layout';
import {
  getPositionAndMaxHeightVertically,
  ModalBox,
} from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import {
  CSSProperties,
  KeyboardEventHandler,
  ReactNode,
  useRef,
  useState,
} from 'react';
import { useEffectToHideEditorOnHorizontalScrollForReferenceEditors } from './useEffectToHideEditorOnHorizontalScrollForPopupEditors';
import { IconDecorator } from '@ardoq/decorators';
import { IconName } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { AsyncSelect, SelectInstance } from '@ardoq/select';
import { useEffectOnce } from '@ardoq/hooks';
import { cellRendererPropsOperations } from '../CellRenderers/cellRendererPropsOperations';
import { ComponentTags } from '../CellRenderers/ComponentTags';
import { cellEditorPropsOperations } from './cellEditorPropsOperations';
import { RadioGroup } from '@ardoq/forms';
import { Text } from '@ardoq/typography';
import { ErrorBoundary } from '@ardoq/error-boundary';
import {
  ColumnDefinitionArdoqMetadata,
  InventoryReferenceRepresentation,
  MetamodelTriplesWithOrigin,
} from '../types';
import { ComponentTag } from '../CellRenderers/ComponentTag';
import { localeCompare } from '@ardoq/locale';
import { TripleWarning } from '@ardoq/metamodel-constraints';
import { ComponentSuggestionWithBreadcrumbs } from '@ardoq/suggestions-api';
import { t, type I18nLocale } from '@ardoq/i18n';
type ComponentTypeOptionWithWarning = {
  componentType: MetamodelComponentType;
  shouldDisplayWarning: boolean;
};

type HeaderContentProps = {
  onCloseClick: () => void;
  children: ReactNode;
  i18nLocale: I18nLocale;
};
const HeaderContent = ({
  onCloseClick,
  children,
  i18nLocale,
}: HeaderContentProps) => (
  <FlexBox justify="space-between" padding="medium">
    {children}
    <IconButton
      iconName={IconName.CLOSE}
      color={colors.icon}
      label={t(
        i18nLocale,
        'inventory:referenceEditor.closeReferenceEditor',
        'Close reference editor'
      )}
      onClick={onCloseClick}
    />
  </FlexBox>
);

const deleteReferenceLocally = (
  props: CellEditorProps<InventoryReferenceRepresentation[]>,
  referencedComponentId: ArdoqId,
  isOutgoingReference: boolean
) => {
  // When there are duplicate references, we only want to remove the first one from the list
  const referenceComponentIds =
    props.value?.map(it => (isOutgoingReference ? it.target : it.source)) ?? [];
  const firstIndexOfComponentIdInValues = referenceComponentIds.indexOf(
    referencedComponentId
  );
  const updatedReferencedComponentIds = props.value?.filter(
    (_, index) => index !== firstIndexOfComponentIdInValues
  );
  props.onValueChange(updatedReferencedComponentIds);
};

const createReferenceLocally = (
  props: CellEditorProps<InventoryReferenceRepresentation[]>,
  referencedComponentId: string,
  componentSuggestions: ComponentSuggestionWithBreadcrumbs[],
  referenceColumnDirection: 'outgoing' | 'incoming',
  referenceTypeName: string
) => {
  const currentComponent = props.data!;
  const scopeData = cellRendererPropsOperations.getScopeData(props);

  const newReference = {
    ...cellEditorPropsOperations.toSourceTargetAndReferenceType(
      scopeData,
      currentComponent,
      referencedComponentId,
      referenceColumnDirection,
      referenceTypeName,
      componentSuggestions
    ),
    direction: referenceColumnDirection,
  };

  props.onValueChange([...props.value!, newReference]);
};

const useTriples = (
  props: CellEditorProps<InventoryReferenceRepresentation[]>,
  isOutgoingReference: boolean,
  referenceTypeName: string
): {
  componentTypeOptions: ComponentTypeOptionWithWarning[];
  selectedComponentType: ComponentTypeOptionWithWarning | null;
  setSelectedComponentType: (typeName: string) => void;
} => {
  const currentComponentTypeName =
    cellEditorPropsOperations.getComponentTypeNameById(
      props,
      props.data.typeId ||
        props.context.scopeData.componentsById[props.data._id].typeId
    );
  const {
    config: { locale },
    metaModel,
    scopeData,
  } = props.context;

  const componentTypesByName: Record<string, MetamodelComponentType> =
    metaModel.componentTypes.reduce(
      (acc, componentType) => ({ ...acc, [componentType.name]: componentType }),
      {}
    );

  const filterMatchingTriplesWithSourceComponentTypeAndReferenceType = (
    triple: MetamodelTriplesWithOrigin
  ) => {
    return (
      triple.sourceComponentType === currentComponentTypeName &&
      triple.referenceType === referenceTypeName &&
      enhancedScopeDataOperations.referenceTypeExistsInModel(
        scopeData,
        props.data.model,
        referenceTypeName
      )
    );
  };

  const filterMatchingTriplesWithTargetComponentTypeAndReferenceType = (
    triple: MetamodelTriplesWithOrigin
  ) => {
    return (
      triple.targetComponentType === currentComponentTypeName &&
      triple.referenceType === referenceTypeName
    );
  };

  const nonUniqueComponentTypeOptions: ComponentTypeOptionWithWarning[] =
    metaModel.triples
      .filter(
        isOutgoingReference
          ? filterMatchingTriplesWithSourceComponentTypeAndReferenceType
          : filterMatchingTriplesWithTargetComponentTypeAndReferenceType
      )
      .map(triple => ({
        componentType:
          componentTypesByName[
            isOutgoingReference
              ? triple.targetComponentType
              : triple.sourceComponentType
          ],
        shouldDisplayWarning: triple.shouldDisplayWarning ?? false,
      }))
      .filter(option => option.componentType);

  const componentTypeOptions = uniqBy(
    nonUniqueComponentTypeOptions,
    option => option.componentType.name
  ).sort((a, b) =>
    localeCompare(a.componentType.name, b.componentType.name, locale)
  );

  const [selectedComponentType, setSelectedComponentType] = useState(
    componentTypeOptions[0]
  );

  return {
    componentTypeOptions,
    selectedComponentType,
    setSelectedComponentType: (typeName: string) => {
      const option = componentTypeOptions.find(
        opt => opt.componentType.name === typeName
      );
      if (option) {
        setSelectedComponentType(option);
      }
    },
  };
};

type ComponentTypeRadioGroupProps = {
  componentTypeOptions: ComponentTypeOptionWithWarning[];
  isOutgoingReference: boolean;
  selectedComponentType: ComponentTypeOptionWithWarning;
  setSelectedComponentType: (typeName: string) => void;
  i18nLocale: I18nLocale;
};

const ComponentTypeRadioGroup = ({
  componentTypeOptions,
  selectedComponentType,
  isOutgoingReference,
  setSelectedComponentType,
  i18nLocale,
}: ComponentTypeRadioGroupProps) => {
  return (
    <FlexBox flexDirection="column" gap="medium" padding="medium">
      <Text variant="text1Bold">
        {isOutgoingReference
          ? t(
              i18nLocale,
              'inventory:referenceEditor.selectTargetComponentType',
              'Select target component type'
            )
          : t(
              i18nLocale,
              'inventory:referenceEditor.selectSourceComponentType',
              'Select source component type'
            )}
      </Text>
      <FlexBox borderRadius="r4" borderWidth={1} padding="medium">
        <RadioGroup
          options={componentTypeOptions.map(option => ({
            value: option.componentType.name,
            label: (
              <ComponentTag
                name={option.componentType.name}
                style={option.componentType.style}
              />
            ),
          }))}
          value={selectedComponentType.componentType.name}
          onValueChange={selectedComponentType =>
            setSelectedComponentType(selectedComponentType as string)
          }
        />
      </FlexBox>
    </FlexBox>
  );
};

export const ReferenceEditor: CellEditor<
  InventoryReferenceRepresentation[]
> = props => {
  useEffectToHideEditorOnHorizontalScrollForReferenceEditors(props.api);

  const selectRef = useRef<SelectInstance<ArdoqId>>(null);
  const componentSuggestions = useRef<
    Array<ComponentSuggestionWithBreadcrumbs>
  >([]);

  useEffectOnce(() => {
    // Focus the select input when the editor is opened
    selectRef.current?.focus();
    // Ensure the column is visible when the editor is opened.
    if (props.colDef.field) props.api.ensureColumnVisible(props.colDef.field);
  });

  const columnContext = props.colDef.context as ColumnDefinitionArdoqMetadata;
  if (columnContext.inventoryColumnType !== 'reference') {
    return null;
  }
  const referenceTypeName = columnContext.referenceTypeName;
  const isOutgoingReference = columnContext.direction === 'outgoing';

  const {
    selectedComponentType,
    componentTypeOptions,
    setSelectedComponentType,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useTriples(props, isOutgoingReference, referenceTypeName);
  const { commands } = props.context;

  const metamodelConstraintsLinkProps =
    commands.getMetamodelConstraintsLinkProps();
  const tripleWarningLabels = commands.getTripleWarningLabels?.();

  const getHostBoundingClientRect = () => {
    const popupParent = props.api.getGridOption('popupParent');
    const popupParentRect = popupParent?.getBoundingClientRect();
    const cellRect = props.eGridCell.getBoundingClientRect();
    if (!popupParentRect) {
      return cellRect;
    }

    // Calculate position relative to popup parent
    return {
      top: cellRect.top - popupParentRect.top,
      bottom: cellRect.bottom - popupParentRect.top,
      left: cellRect.left,
      right: cellRect.right,
      width: cellRect.width,
      height: cellRect.height,
    };
  };

  const hostRect = getHostBoundingClientRect();
  const cellSizedStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '0 15px',
    width: hostRect?.width ?? 200,
    height: hostRect?.height ?? 40,
  };

  const scopeData = cellRendererPropsOperations.getScopeData(props);

  const getOnComponentTagBackspaceClick =
    (componentId: ArdoqId): KeyboardEventHandler<HTMLElement> =>
    keyDownEvent => {
      if (keyDownEvent.key === 'Backspace') {
        keyDownEvent.stopPropagation();
        keyDownEvent.preventDefault();
        deleteReferenceLocally(props, componentId, isOutgoingReference);
      }
    };

  const canEditReference = (referencedComponentId: ArdoqId) => {
    const { source, target, type } =
      cellEditorPropsOperations.toSourceTargetAndReferenceType(
        scopeData,
        props.data!,
        referencedComponentId,
        columnContext.direction,
        referenceTypeName,
        componentSuggestions.current
      );

    const referenceToEdit = scopeData.references.find(
      reference =>
        reference.source === source &&
        reference.target === target &&
        reference.type === type
    );

    if (!referenceToEdit) return true; // we can remove a reference that is not in scope data - it was just created

    return props.context.accessControl.canEditReference(referenceToEdit);
  };
  const currentComponentTypeName =
    cellEditorPropsOperations.getComponentTypeNameById(
      props,
      props.data.typeId ||
        props.context.scopeData.componentsById[props.data._id].typeId
    );

  const existingReferencedIds = new Set(
    props.value?.map(it => (isOutgoingReference ? it.target : it.source)) ?? []
  );

  const sourceTypeName = isOutgoingReference
    ? (currentComponentTypeName ?? '')
    : (selectedComponentType?.componentType.name ?? '');
  const targetTypeName = isOutgoingReference
    ? (selectedComponentType?.componentType.name ?? '')
    : (currentComponentTypeName ?? '');

  return (
    <div style={cellSizedStyle}>
      <ErrorBoundary>
        <ComponentTags
          componentIds={
            props.value?.map(it =>
              isOutgoingReference ? it.target : it.source
            ) || []
          }
          scopeData={scopeData}
          getOpenComponentOverview={cellRendererPropsOperations.getOpenComponentOverview(
            props.context
          )}
          onCloseClick={componentId =>
            deleteReferenceLocally(props, componentId, isOutgoingReference)
          }
          componentSuggestions={componentSuggestions.current}
          canEditReference={canEditReference}
          getOnBackspaceClick={getOnComponentTagBackspaceClick}
          i18nLocale={props.context.i18nLocale}
        />
        {selectedComponentType ? (
          <ModalBox
            role="dialog"
            aria-label={t(
              props.context.i18nLocale,
              'inventory:referenceEditor.createReference',
              'Create Reference'
            )}
            dropdownSize={DropdownSize.M}
            getHostBox={getHostBoundingClientRect}
            getPositionAndMaxHeight={(refBox, modalBox) =>
              getPositionAndMaxHeightVertically({
                refBox,
                modalBox,
                isScrollable: true,
                forceAlignToRight: false,
              })
            }
            headerContent={
              <HeaderContent
                onCloseClick={() => props.api.stopEditing()}
                i18nLocale={props.context.i18nLocale}
              >
                <IconDecorator iconName={IconName.REFERENCE}>
                  {t(
                    props.context.i18nLocale,
                    'inventory:referenceEditor.createReference',
                    'Create Reference'
                  )}
                </IconDecorator>
              </HeaderContent>
            }
            footerContent={
              <FlexBox flexDirection="column">
                <FlexBox paddingX="medium" paddingTop="medium">
                  <Text variant="text1Bold">
                    {isOutgoingReference
                      ? t(
                          props.context.i18nLocale,
                          'inventory:referenceEditor.selectTargetComponentNamed',
                          {
                            typeName: selectedComponentType.componentType.name,
                          },
                          "Select '{{typeName}}' target component"
                        )
                      : t(
                          props.context.i18nLocale,
                          'inventory:referenceEditor.selectSourceComponentNamed',
                          {
                            typeName: selectedComponentType.componentType.name,
                          },
                          "Select '{{typeName}}' source component"
                        )}
                  </Text>
                </FlexBox>
                <AsyncSelect
                  key={selectedComponentType.componentType.name} // we want to dump previous options and fetch new default ones when user changes component type
                  selectRef={selectRef}
                  placeholder={t(
                    props.context.i18nLocale,
                    'inventory:referenceEditor.searchComponents',
                    'Search components'
                  )}
                  defaultOptions={true}
                  noOptionsMessage={() =>
                    t(
                      props.context.i18nLocale,
                      'inventory:referenceEditor.noOptionsMessage',
                      'Suggestions come from the defined metamodel constraints for your organization and workspaces where that reference type is defined.'
                    )
                  }
                  loadOptions={debounce(
                    async searchPhrase => {
                      const fetchComponentSuggestionsForReference =
                        isOutgoingReference
                          ? commands.getTargetComponentSuggestions(
                              selectedComponentType.componentType.name
                            )
                          : commands.getSourceComponentSuggestions(
                              selectedComponentType.componentType.name,
                              referenceTypeName
                            );
                      const newComponentSuggestions =
                        await fetchComponentSuggestionsForReference(
                          searchPhrase
                        );
                      componentSuggestions.current = [
                        ...componentSuggestions.current,
                        ...newComponentSuggestions,
                      ];
                      return newComponentSuggestions;
                    },
                    100,
                    {
                      leading: true,
                    }
                  )}
                  filterOption={option =>
                    !existingReferencedIds.has(option.value)
                  }
                  onValueChange={componentId =>
                    componentId &&
                    createReferenceLocally(
                      props,
                      componentId,
                      componentSuggestions.current,
                      columnContext.direction,
                      referenceTypeName
                    )
                  }
                />
                {selectedComponentType.shouldDisplayWarning && (
                  <TripleWarning
                    sourceType={sourceTypeName}
                    referenceType={referenceTypeName}
                    targetType={targetTypeName}
                    canManageMetamodelConstraints={
                      false /* TODO add isAdmin-check */
                    }
                    manageConstraintsLinkProps={metamodelConstraintsLinkProps}
                    labels={tripleWarningLabels}
                  />
                )}
              </FlexBox>
            }
          >
            <ComponentTypeRadioGroup
              componentTypeOptions={componentTypeOptions}
              isOutgoingReference={isOutgoingReference}
              selectedComponentType={selectedComponentType}
              setSelectedComponentType={setSelectedComponentType}
              i18nLocale={props.context.i18nLocale}
            />
          </ModalBox>
        ) : (
          <ModalBox
            role="dialog"
            aria-label={t(
              props.context.i18nLocale,
              'inventory:referenceEditor.noAvailableComponentTypes',
              'No available component types'
            )}
            dropdownSize={DropdownSize.M}
            getHostBox={getHostBoundingClientRect}
            getPositionAndMaxHeight={(refBox, modalBox) =>
              getPositionAndMaxHeightVertically({
                refBox,
                modalBox,
                isScrollable: true,
                forceAlignToRight: false,
              })
            }
          >
            <HeaderContent
              onCloseClick={() => props.api.stopEditing()}
              i18nLocale={props.context.i18nLocale}
            >
              {t(
                props.context.i18nLocale,
                'inventory:referenceEditor.noAvailableComponentTypesMessage',
                'There are no available component types to create this reference. Check your metamodel configuration.'
              )}
            </HeaderContent>
          </ModalBox>
        )}
      </ErrorBoundary>
    </div>
  );
};
